import { Component, HostListener, OnInit, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs/operators';

// PrimeNG Components
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';

// Shared Components
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { ContextualHelpComponent } from './shared/components/contextual-help/contextual-help.component';
import { FeedbackWidgetComponent } from './shared/components/feedback-widget/feedback-widget.component';
import { UnsafeRepricingConfirmModalComponent } from './features/repricing/manual/components/unsafe-repricing-confirm-modal/unsafe-repricing-confirm-modal.component';

// Services
import { WebSocketBootstrapService } from './core/services/websocket-bootstrap.service';
import { LazyStylesService } from './core/services/lazy-styles.service';
import { AccessibilityService } from './core/services/accessibility.service';
import { DemoModeService } from './core/services/demo-mode.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    ToastModule,
    ButtonModule,
    TranslateModule,
    SidebarComponent,
    HeaderComponent,
    ContextualHelpComponent,
    FeedbackWidgetComponent,
    UnsafeRepricingConfirmModalComponent
  ],
  template: `
    <a class="skip-link" href="#main-content">Перейти к основному содержимому</a>

    <div
      class="app-layout"
      [class.with-sidebar]="showLayout"
      [class.sidebar-collapsed]="showLayout && effectiveSidebarCollapsed()"
      [class.demo-active]="showLayout && demoMode.isDemo()"
    >
      <p-toast></p-toast>
      <app-unsafe-repricing-confirm-modal></app-unsafe-repricing-confirm-modal>

      @defer (when showLayout) {
        <!-- Header - only on authenticated pages (NOT on /welcome) -->
        <app-header
          [navigationExpanded]="navigationExpanded()"
          (sidebarToggle)="toggleSidebar()"
        ></app-header>

        @if (demoMode.isDemo()) {
          <div class="demo-mode-banner" role="status" aria-live="polite">
            <span>{{ 'demo.banner' | translate }}</span>
            <button
              pButton
              type="button"
              class="p-button-sm p-button-warning demo-mode-disable"
              (click)="demoMode.disable()"
              [label]="'demo.disable' | translate"
            ></button>
          </div>
        }

        <!-- Sidebar - only on authenticated pages (NOT on /welcome) -->
        <app-sidebar
          [collapsed]="effectiveSidebarCollapsed()"
          [class.open]="isMobileLayout() && sidebarOpen()"
        ></app-sidebar>

        @if (isMobileLayout() && sidebarOpen()) {
          <button
            type="button"
            class="sidebar-backdrop"
            aria-label="Закрыть боковую навигацию"
            (click)="closeSidebar()"
          ></button>
        }

        <!-- Feedback Widget (Story 15.12) - only on authenticated pages -->
        <app-feedback-widget></app-feedback-widget>

        <app-contextual-help class="route-help-fab"></app-contextual-help>
      }

      <!-- Main Content -->
      <main
        id="main-content"
        class="main-content"
        [class.with-sidebar]="showLayout"
        [class.sidebar-collapsed]="effectiveSidebarCollapsed()"
        tabindex="-1"
        aria-label="Основное содержимое"
      >
        <router-outlet></router-outlet>
      </main>
    </div>
  `,
  styles: [
    `
      .app-layout {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }

      .app-layout.with-sidebar {
        display: grid;
        grid-template-columns: 260px 1fr;
        grid-template-rows: 60px 1fr;
        grid-template-areas:
          'header header'
          'sidebar content';
      }

      .app-layout.with-sidebar.sidebar-collapsed {
        grid-template-columns: 60px 1fr;
      }

      .app-layout.with-sidebar.demo-active {
        grid-template-rows: 60px 44px 1fr;
        grid-template-areas:
          'header header'
          'demo demo'
          'sidebar content';
      }

      :host ::ng-deep app-header {
        grid-area: header;
        position: sticky;
        top: 0;
        z-index: 1000;
      }

      .demo-mode-banner {
        grid-area: demo;
        position: sticky;
        top: 60px;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        min-height: 44px;
        padding: 0.35rem 1rem;
        border-bottom: 2px solid #b45309;
        background: #fbbf24;
        color: #451a03;
        font-weight: 700;
        text-align: center;
      }

      .demo-mode-disable {
        min-height: 2rem;
        color: #451a03;
        background: #f59e0b;
        border-color: #b45309;
      }

      :host ::ng-deep app-sidebar {
        grid-area: sidebar;
        position: sticky;
        top: 60px;
        height: calc(100vh - 60px);
        overflow-y: auto;
      }

      .app-layout.demo-active ::ng-deep app-sidebar {
        top: 104px;
        height: calc(100vh - 104px);
      }

      :host ::ng-deep app-contextual-help.route-help-fab {
        position: fixed;
        top: 76px;
        right: 18px;
        z-index: 1002;
      }

      .main-content {
        grid-area: content;
        padding: 1.5rem;
        background-color: var(--color-bg, var(--surface-ground, #f8fafc));
        min-height: calc(100vh - 60px);
      }

      .main-content:not(.with-sidebar) {
        padding: 0;
        min-height: 100vh;
      }

      /* Responsive */
      .sidebar-backdrop {
        position: fixed;
        top: 60px;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 998;
        border: 0;
        padding: 0;
        background: rgba(15, 23, 42, 0.48);
      }

      @media (min-width: 768px) and (max-width: 1023px) {
        .app-layout.with-sidebar {
          grid-template-columns: 60px 1fr;
        }

        .app-layout.with-sidebar:not(.sidebar-collapsed) {
          grid-template-columns: 260px 1fr;
        }

        .main-content {
          padding: 1rem;
        }
      }

      @media (max-width: 767px) {
        .app-layout.with-sidebar {
          grid-template-columns: 1fr;
          grid-template-areas:
            'header'
            'content';
        }

        .app-layout.with-sidebar.demo-active {
          grid-template-rows: 60px 44px 1fr;
          grid-template-areas:
            'header'
            'demo'
            'content';
        }

        :host ::ng-deep app-sidebar {
          position: fixed;
          left: 0;
          top: 60px;
          transition: transform 0.3s ease;
          z-index: 999;
        }

        .app-layout.demo-active ::ng-deep app-sidebar {
          top: 104px;
          height: calc(100vh - 104px);
        }

        .demo-mode-banner {
          gap: 0.5rem;
          padding: 0.35rem 0.75rem;
          font-size: 0.85rem;
        }

        .main-content {
          padding: 0.75rem;
        }

        :host ::ng-deep app-contextual-help.route-help-fab {
          top: 68px;
          right: 12px;
        }
      }
    `
  ]
})
export class AppComponent implements OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private router = inject(Router);
  private readonly lazyStyles = inject(LazyStylesService);
  private readonly accessibility = inject(AccessibilityService);
  readonly demoMode = inject(DemoModeService);

  readonly isMobileLayout = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map(state => state.matches)),
    { initialValue: false }
  );
  readonly isTabletLayout = toSignal(
    this.breakpointObserver
      .observe('(min-width: 768px) and (max-width: 1023px)')
      .pipe(map(state => state.matches)),
    { initialValue: false }
  );
  readonly sidebarCollapsed = signal(false);
  readonly sidebarOpen = signal(false);
  readonly tabletSidebarExpanded = signal(false);
  readonly effectiveSidebarCollapsed = computed(() => {
    if (this.isMobileLayout()) {
      return false;
    }

    if (this.isTabletLayout()) {
      return !this.tabletSidebarExpanded();
    }

    return this.sidebarCollapsed();
  });
  readonly navigationExpanded = computed(() =>
    this.isMobileLayout() ? this.sidebarOpen() : !this.effectiveSidebarCollapsed()
  );
  showLayout = false; // Показывать ли sidebar/header (НЕ на /welcome, НЕ на /auth)

  constructor(private readonly wsBootstrap: WebSocketBootstrapService) {
    this.wsBootstrap.initialize();
    this.updateLayoutVisibility(this.router.url);
    void this.accessibility;

    effect(() => {
      const mobile = this.isMobileLayout();
      const tablet = this.isTabletLayout();
      // Use untracked to avoid NG0600 (writing signals inside effect)
      queueMicrotask(() => {
        if (!mobile) this.sidebarOpen.set(false);
        if (!tablet) this.tabletSidebarExpanded.set(false);
      });
    });

    // CRITICAL: Track route changes для показа/скрытия layout
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.updateLayoutVisibility(event.urlAfterRedirects);
        this.closeSidebar();
      });
  }

  ngOnInit(): void {
    this.lazyStyles.loadMaterialTheme();
    this.updateLayoutVisibility(this.router.url);
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isMobileLayout() && this.sidebarOpen()) {
      this.closeSidebar();
    }
  }

  toggleSidebar(): void {
    if (this.isMobileLayout()) {
      this.sidebarOpen.update(value => !value);
      return;
    }

    if (this.isTabletLayout()) {
      this.tabletSidebarExpanded.update(value => !value);
      return;
    }

    this.sidebarCollapsed.update(value => !value);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  private updateLayoutVisibility(url: string): void {
    this.showLayout = !this.isPublicRoute(url);
  }

  private isPublicRoute(url: string): boolean {
    return (
      url === '/' ||
      url.startsWith('/welcome') ||
      url.startsWith('/auth') ||
      url.startsWith('/register')
    );
  }
}
